import React from 'react';
import { FolderType, FileType, SortState } from '../AccountFiles';
import { Button, Flex, Icon, Text } from '@upsales/components';
import BemClass from '@upsales/components/Utils/bemClass';
import T from 'Components/Helpers/translate';
import Folder from '../Folder';
import File from '../File';
import './GridView.scss';

type Props = {
	folders: FolderType[];
	files: FileType[];
	openFolder: (folderId: number, name: string) => void;
	deleteFolder: (id: number) => void;
	deleteFile: (file: FileType) => void;
	changeFolderFolder: (id: number, folderId: number | null) => void;
	changeFileFolder: (id: number, folderId: number | null) => void;
	changeNameOfFolder: (id: number, name: string) => void;
	sort: SortState;
	onSortChange: (sort: SortState) => void;
	folderPaginator?: React.ReactNode;
	filesDraggable?: boolean;
};

const GridView = ({
	files,
	folders,
	openFolder,
	deleteFolder,
	deleteFile,
	changeFolderFolder,
	changeFileFolder,
	changeNameOfFolder,
	sort,
	onSortChange,
	folderPaginator,
	filesDraggable = true
}: Props) => {
	const classes = new BemClass('FilesGridView');

	return (
		<div className={classes.b()}>
			{folders.length ? (
				<div className={classes.elem('folders-container').b()}>
					<Flex direction="row" gap="u4">
						<Text align="center" size="xl">
							{T('file.folders')}
						</Text>
					</Flex>
					<div className={classes.elem('folders-container__folders').b()}>
						{folders.map(folder => (
							<Folder
								deleteFolder={deleteFolder}
								key={folder.id}
								openFolder={openFolder}
								id={folder.id}
								name={folder.name}
								isSystem={folder.isSystem}
								numberOfFolders={folders.length}
								numberOfFiles={files.length}
								changeFolderFolder={changeFolderFolder}
								changeFileFolder={changeFileFolder}
								changeNameOfFolder={changeNameOfFolder}
							/>
						))}
					</div>
				</div>
			) : null}
			{folderPaginator}
			<div className={classes.elem('files-container').b()}>
				{files.length ? (
					<Flex direction="row" gap="u4">
						<Text size="xl">{T('file.files')}</Text>
						<Button
							type="link"
							size="sm"
							onClick={() =>
								onSortChange({ field: 'filename', asc: sort.field === 'filename' ? !sort.asc : true })
							}
							className={classes
								.elem('sort-button')
								.mod({ name: sort.field === 'filename' })
								.b()}
						>
							{T('default.name')}
							<Icon
								space="mls"
								name={
									sort.field !== 'filename' ? 'sort' : sort.asc ? 'sort-alpha-asc' : 'sort-alpha-desc'
								}
							/>
						</Button>
						<Button
							type="link"
							size="sm"
							onClick={() =>
								onSortChange({ field: 'size', asc: sort.field === 'size' ? !sort.asc : true })
							}
							className={classes
								.elem('sort-button')
								.mod({ size: sort.field === 'size' })
								.b()}
						>
							{T('default.size')}
							<Icon
								space="mls"
								name={
									sort.field !== 'size' ? 'sort' : sort.asc ? 'sort-numeric-asc' : 'sort-numeric-desc'
								}
							/>
						</Button>
						<Button
							type="link"
							size="sm"
							onClick={() =>
								onSortChange({
									field: 'uploadDate',
									asc: sort.field === 'uploadDate' ? !sort.asc : true
								})
							}
							className={classes
								.elem('sort-button')
								.mod({ date: sort.field === 'uploadDate' })
								.b()}
						>
							{T('default.date')}
							<Icon
								space="mls"
								name={
									sort.field !== 'uploadDate'
										? 'sort'
										: sort.asc
										? 'sort-numeric-asc'
										: 'sort-numeric-desc'
								}
							/>
						</Button>
					</Flex>
				) : null}
				<div className={classes.elem('files-container__files').b()}>
					{files.map(file => (
						<File
							deleteFile={() => deleteFile(file)}
							key={file.id}
							id={file.id}
							fileType={file.extension}
							name={file.filename}
							userRemovable={file.userRemovable}
							draggable={filesDraggable}
						/>
					))}
				</div>
			</div>
		</div>
	);
};

export default GridView;
